import { ArrowRightOutlined } from '@ant-design/icons';
import {
  actionButtons,
  farmStagesTitles,
} from 'constants/FarmboxConstants/stage0.constants';
import { ButtonType } from 'enums/ButtonEnums';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import CardWithStickyHeader, {
  ButtonProps,
} from 'shared/components/CardWithStickyHeader';
import styles from './farmFieldFlow.module.scss';
import FarmFieldService from 'services/FarmFieldService/farmField.service';
import { FieldDetails } from 'models/Farmbox/field.model';
import { LandMetaData } from 'constants/FarmboxConstants/stage4.constants';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import CropResidueSection from './CropResidue';
import SoilOrganicCarbonSection from './SoilCarbon';
import LandUseChangeSection from './LandUseChange';
import LandManagementChangeSection from './LandManagementChange';
import isProductDisabled from 'shared/utils/isProductDisabled';
import { HelpDeskContext } from 'context/HelpDeskContext';

const FarmField = () => {
  const {
    loading,
    buttonLoading,
    landUseMetaData,
    landManagementMetaData,
    fetchFieldData,
    updateFieldData,
    fetchLandMetaData,
  } = FarmFieldService();
  const { id: commodityId, status } = useParams();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { handleSubmit } = useFormContext();
  const { setIsHelpDeskOpen } = useContext(HelpDeskContext);
  const { redirectToFarmFuelAndEnergy, redirectToFarmCommodityDistribution } = useFarmboxRedirections();
  const [harvestYear, setHarvestYear] = useState<number | undefined>();

  useEffect(() => {
    fetchData();
  }, []);

  const { FIELD } = farmStagesTitles;
  const { PRIMARY } = ButtonType;
  const { NEXT } = actionButtons;
  const isDisabled = isProductDisabled(status);

  const fetchData = async () => {
    await fetchLandMetaData(LandMetaData.LAND_USE_META);
    await fetchLandMetaData(LandMetaData.LAND_MANAGEMENT_META);
    const fieldDataResponse = await fetchFieldData({ commodityId });
    if (fieldDataResponse?.commodity?.harvestYear) {
      setHarvestYear(fieldDataResponse.commodity.harvestYear);
    }
  };

  const handleFormSubmit: SubmitHandler<FieldDetails> = async (
    formData: FieldDetails
  ) => {
    if (!commodityId) {
      return;
    }
    formData?.commodity?.landUseChanges?.forEach(landUse => {
      const metadata = landUseMetaData.find(
        meta => meta.label === landUse.label
      );
      if (metadata) {
        landUse.from = metadata.from;
        landUse.to = metadata.to;
      }
    });
    formData?.commodity?.landManagementChanges?.forEach(landManagement => {
      const metadata = landManagementMetaData.find(
        meta => meta.label === landManagement.label
      );
      if (metadata) {
        landManagement.from = metadata.from;
        landManagement.to = metadata.to;
      }
    });

    if (!isDisabled) {
      const success = await updateFieldData(commodityId, formData);
      if (success)
        redirectToFarmCommodityDistribution(
          Number(commodityId),
          status,
          queryParams
        );
    } else {
      redirectToFarmCommodityDistribution(
        Number(commodityId),
        status,
        queryParams
      );
    }
  };

  const actionButton: ButtonProps[] = [
    {
      icon: <ArrowRightOutlined />,
      title: NEXT,
      clickCallback: () => {
        handleSubmit(handleFormSubmit)();
      },
      type: PRIMARY,
      submit: true,
      loading: buttonLoading,
    },
  ];

  const handleBackNavigation = () => {
    setIsHelpDeskOpen(false);
    redirectToFarmFuelAndEnergy(Number(commodityId), status, queryParams);
  };

  return (
    <div className={styles.fieldContainer}>
      <div className={styles.fieldCardContainer}>
        <CardWithStickyHeader
          title={FIELD}
          actionButtons={actionButton}
          backCallback={handleBackNavigation}
          loading={loading}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className={styles.fieldCardContentContainer}>
              <CropResidueSection />
              <SoilOrganicCarbonSection />
            </div>
            <LandUseChangeSection
              landUseMetaData={landUseMetaData}
              harvestYear={harvestYear}
            />
            <LandManagementChangeSection
              landManagementMetaData={landManagementMetaData}
              harvestYear={harvestYear}
            />
          </form>
        </CardWithStickyHeader>
      </div>
    </div>
  );
};

export default FarmField;
